import PAGES from './pages/pages'

import { AsyncPagesFactories, Route, RouteConfiguration } from 'navigation'
import AsyncPage from 'pages/AsyncPage'
import { NOT_FOUND } from 'redux-first-router'

const isDefined = str => typeof str === 'string'

export const flatten = (arr, result = new Set()) => {
  if (arr && arr.length) {
    arr.forEach(e => {
      if (e) {
        result.add(e.type || e)
        if (e.ancestry && e.ancestry.length) {
          flatten(e.ancestry, result)
        }
      }
    })
  }
  return result
}

export const toArray = set => {
  let result = []
  set.forEach(e => result.push(e))
  return result
}

export const flattenAncestry = (arr: Route[]): Route[] => {
  return arr.map(route => {
    route.ancestry = toArray(flatten(route.ancestry))
    return route
  })
}

let uniq = a => Array.from(new Set(a))

export const prune_ancestry = (arr, delegates) => {
  return arr.map(route => {
    route.ancestry = uniq(route.ancestry.map(a => delegates[a] || a)).filter(
      e => e !== route.type
    )
    return route
  })
}



export const createRoutesArray = (
  places: RouteConfiguration[],
  prepare,
  pages: AsyncPagesFactories,
  path: string = '',
  ancestry: Route[] = [],
  delegates = {}
): Route[] => {
  return flattenAncestry(
    places.map((place: RouteConfiguration, index) => {
      let routePath = `${path}/${isDefined(place.path)
        ? place.path
        : place.name}`.replace(/\/\//g, '/')
      let parent = ancestry.length && ancestry[ancestry.length - 1]
      let pageName = parent ? `${parent.type}/${place.name}` : place.name
      let page = pages[pageName]
      if (!page && !place.delegateToFirstChild) {
        throw new Error(
          `Available pages: \r\n  ${Object.keys(pages).join(',\r\n  ')}\r\n` +
          `missing page "${pageName}" for path "${routePath}" - update 'pages/index.js' to map "${pageName}" to an AsyncPage.`
        )
      }

      let routeValue: Route = {
        ancestry: ancestry.slice(0),
        icon: place.icon || parent.icon,
        modal: !!place.modal,
        type: pageName,
        path: routePath,
        large_screen: !!place.large_screen, // should this place show as primary nav on large screens?
        thunk: prepare(page)
      }

      if (place.prev) {
        routeValue.prev =
          place.prev.indexOf('/') >= 0
            ? place.prev
            : `${parent.type}/${place.prev}`
      }

      if (place.next) {
        routeValue.next =
          place.next.indexOf('/') >= 0
            ? place.next
            : `${parent.type}/${place.next}`
      }

      if (place.children && place.children.length) {
        let subRoutes = createRoutesArray(
          place.children,
          prepare,
          pages,
          routePath,
          ancestry.concat(routeValue),
          delegates
        )
        if (place.delegateToFirstChild) {
          // re-map first child to here
          let delegate = subRoutes[0]
          delegates[pageName] = delegate.type
          delegate.path = routePath
          delegate.modal = place.modal || delegate.modal
          delegate.large_screen = place.large_screen || delegate.large_screen
          if (delegate.ancestry[delegate.ancestry.length - 1] === pageName) {
            delegate.ancestry = delegate.ancestry.slice(
              0,
              delegate.ancestry.length - 1
            ) // remove the last, because it is us
          }
          delegate.children = (delegate.children || []).concat(
            subRoutes.slice(1)
          )
          return delegate
        } else {
          routeValue.children = subRoutes
        }
      }
      return routeValue
    })
  )
}

export const convertToMap = (result, route): Record<string, Route> => {
  result[route.type] = route
  if (route.children && route.children.length) {
    result = route.children.reduce(convertToMap, result)
    delete route.children
  }
  return result
}

export const createRoutesMap = (places: RouteConfiguration[], prepare, pages: AsyncPagesFactories): Record<string, Route> => {
  let delegates = {}
  let routesArray = createRoutesArray(places, prepare, pages, '', [], delegates)
  let map: Record<string, Route> = routesArray.reduce(convertToMap, {})
  prune_ancestry(Object.values(map), delegates)
  return map
}

export const collectSecondaryNavs = (places: RouteConfiguration[], primaries: string[], parent) => {
  return places.reduce((r, p) => {
    if (p.modal) {
      if (p.secondary) {
        let path = [parent, p.name]
        if (p.delegateToFirstChild) {
          path.push(p.children[0].name)
        }
        r[path.join('/')] = [path.join('/')]
      } else {
        if (p.delegateToFirstChild && p.children[0].secondary) {
          let sub = collectSecondaryNavs(
            p.children,
            primaries,
            parent ? `${parent}/${p.name}` : p.name
          )
          sub[`${parent}/${p.name}/${p.children[0].name}`] =
            sub[`${parent}/${p.name}`]
          sub[`${parent}/${p.name}`] = undefined
          r = Object.assign({}, r, sub)
        } else {
          if (p.children) {
            let sub = collectSecondaryNavs(
              p.children,
              primaries,
              parent ? `${parent}/${p.name}` : p.name
            )
            r = Object.assign({}, r, sub)
          }
        }
      }
    } else {
      if (p.secondary) {
        let primary =
          primaries.find(primary => primary.startsWith(parent)) || parent
        let path = [parent, p.name]
        if (!r[primary]) {
          if (p.delegateToFirstChild) {
            path.push(p.children[0].name)
            r[primary] = [path.join('/')]
          } else {
            r[primary] = [path.join('/')]
          }
        } else {
          r[primary].push(path.join('/'))
        }
      }
    }
    if (p.children) {
      let sub = collectSecondaryNavs(
        p.children,
        primaries,
        parent ? `${parent}/${p.name}` : p.name
      )
      r = Object.assign({}, r, sub)
    }
    return r
  }, {})
}

export const collectTertiaryNavs = (places, parent, firstChildParent) => {
  return places.reduce((r, p, i) => {
    if (p.tertiary && parent) {
      if (!r[firstChildParent]) {
        r[firstChildParent] = [`${parent}/${p.name}`]
      } else {
        r[firstChildParent].push(`${parent}/${p.name}`)
      }
    }
    if (p.children) {
      let path = parent ? `${parent}/${p.name}` : p.name
      let firstChildPath = p.delegateToFirstChild
        ? `${parent}/${p.name}/${p.children[0].name}`
        : `${p.name}/${p.children[0].name}`
      r = Object.assign(
        {},
        r,
        collectTertiaryNavs(p.children, path, firstChildPath)
      )
    }
    return r
  }, {})
}

export const primary_nav_reducer = (r: string[], p: RouteConfiguration) => {
  if (p.primary) {
    let _p = p
    let path: string[] = []
    while (_p.delegateToFirstChild) {
      path.push(_p.name)
      _p = _p.children[0]
    }
    path.push(_p.name)
    r.push(path.join('/'))
  }
  return r
}

export const secondary_nav_reducer = places => (r, p: RouteConfiguration) => {
  let parent_name = p.split('/')[0]
  let parent = places.find(place => place.name === parent_name)
  if (parent && parent.children) {
    r[p] = parent.children.reduce((r, c, index) => {
      c.secondary && /* !c.modal && */ r.push(`${parent_name}/${c.name}`)
      return r
    }, [])
  }
  return r
}

export const createNavigationHierarchy = (places: RouteConfiguration[]) => {
  let primary: string[] = places.reduce(primary_nav_reducer, [])
  let secondary = collectSecondaryNavs(places, primary)
  // let secondary = primary.reduce(secondary_nav_reducer(places),{})
  // secondary = Object.assign({}, secondary, collectModalSecondaryNavs(places))
  let tertiary = collectTertiaryNavs(places)
  return {
    primary,
    secondary,
    tertiary
  }
}


export const ROUTE_CONFIGURATION: RouteConfiguration[] = [
  {
    name: 'home',
    path: '/',
    icon: 'icons/HomeIcon',
    primary: true,
    large_screen: true,
    delegateToFirstChild: true,
    children: [
      {
        name: 'news',
        secondary: true,
        children: [
          {
            name: 'tag',
            path: ':tag',
            modal: true,
            delegateToFirstChild: true,
            children: [
              {
                name: 'posts',
                secondary: true
              },
              {
                name: 'circles',
                secondary: true
              }
            ]
          }
        ]
      },
      {
        name: 'events',
        secondary: true
      },      
      {
        name: 'activity',
        secondary: true
      },      
      {
        name: 'people',
        secondary: true
      },
      {
        name: 'privacy',
      },
      {
        name: 'terms',
      },
      {
        name: 'payment-success',
      },
      {
        name: 'payment-cancel',
      },
      {
        name: 'member',
        path: 'members/@:username',
        modal: true,
        secondary: true,
        delegateToFirstChild: true,
        children: [
          {
            name: 'posts',
            tertiary: true,
            children: [
              {
                name: 'post',
                path: ':name',
                modal: true
              }
            ]
          },
          {
            name: 'bio',
            tertiary: true
          },
          {
            name: 'events',
            tertiary: true
          },
          {
            name: 'responses',
            tertiary: true
          }
        ]
      },
      {
        name: 'welcome',
        children: [
          {
            name: 'register',
            children: [
              {
                name: 'email',
                children: [
                  {
                    name: 'confirm-with-token',
                    path: 'confirm-with-token/:token'
                  },
                  {
                    name: 'confirm-with-pin',
                  },                  
                ]
              },
            ]
          },
          {
            name: 'login',
            children: [
              {
                name: 'email'
              },
              {
                name: 'forgot-password'
              }
            ]
          },
          {
            name: 'reset-password',
            path: 'reset-password/:token'
          },
          {
            name: 'invitation',
            path: 'invitation/:token'
          },
          {
            name: 'invitation2',
            path: 'invitation2/:token'
          },
          {
            name: 'change-email',
            path: 'change-email/:token'
          },                             
          {
            name: 'logout'
          }
        ]
      }
    ]
  },
  {
    name: 'notifications',
    primary: true,
    large_screen: true,
    icon: 'icons/NotificationsIcon'
  },
  {
    name: 'create',
    primary: true,
    large_screen: true,
    icon: 'icons/CreateIcon'
  },
  {
    name: 'groups',
    primary: true,
    large_screen: true,
    icon: 'icons/GroupsIcon',
    delegateToFirstChild: true,
    children: [
      {
        name: 'home',
        secondary: true
      },
      {
        name: 'discover',
        secondary: true
      },
      {
        name: 'group',
        path: ':group',
        modal: true,
        secondary: true,
        delegateToFirstChild: true,
        children: [
          {
            name: 'posts',
            tertiary: true,
            children: [
              {
                name: 'post',
                modal: true,
                path: 'post/:post'
              }
            ]
          },
          {
            name: 'events',
            tertiary: true
          },          
          {
            name: 'activity',
            tertiary: true
          },
          {
            name: 'outcome',
            tertiary: true
          },
          {
            name: 'reference',
            tertiary: true
          },
          {
            name: 'groups',
            tertiary: true
          },
          {
            name: 'membership',
            modal: true,
            delegateToFirstChild: true,
            children: [
              {
                name: 'members',
                secondary: true
              },
              {
                name: 'invites',
                secondary: true,
                children: [
                  {
                    name: '_create',
                    modal: true
                  }
                ]
              },
              {
                name: 'access_requests',
                secondary: true
              }
            ]
          },
          {
            name: '_edit',
            modal: true
          }
        ]
      },
      {
        name: '_create',
        modal: true,
        delegateToFirstChild: true,
        children: [
          {
            name: 'basics',
            modal: true,
            next: 'privacy'
          },
          {
            name: 'privacy',
            modal: true,
            prev: 'basics',
            next: 'members'
          },
          {
            name: 'members',
            modal: true,
            prev: 'privacy',
            next: 'sponsor'
          },
          {
            name: 'sponsor',
            modal: true,
            prev: 'members',
            next: 'schedule'
          },
          {
            name: 'schedule',
            modal: true,
            prev: 'sponsor'
          }
        ]
      }
    ]
  },
  {
    name: 'profile',
    primary: true,
    large_screen: false,
    icon: 'icons/PersonIcon',
    delegateToFirstChild: true,
    children: [
      {
        name: 'public',
        secondary: true,
        delegateToFirstChild: true,
        children: [
          {
            name: 'posts',
            tertiary: true
          },
          {
            name: 'events',
            tertiary: true
          },
          {
            name: 'responses',
            tertiary: true
          },
          {
            name: 'bio',
            tertiary: true
          },
          {
            name: '_edit',
            modal: true
          }
        ]
      },
      {
        name: 'drafts',
        secondary: true,
        children: [
          {
            name: '_create',
            path: '_create/:postType/:group?',
            modal: true,
            delegateToFirstChild: true,
            children: [
              {
                name: 'compose',
                next: 'profile/drafts/_edit/tag'
              }
            ]
          },
          {
            name: '_edit',
            path: '_edit/:postType/:postId',
            modal: true,
            delegateToFirstChild: true,
            children: [
              {
                name: 'compose',
                next: 'tag'
              },
              {
                name: 'tag',
                prev: 'compose',
                next: 'audience'
              },
              {
                name: 'audience',
                prev: 'tag',
                next: 'share'
              },
              {
                name: 'share',
                prev: 'audience'
              }
            ]
          },
          {
            name: '_editevent',
            path: '_editevent/:postType/:postId',
            modal: true,
            delegateToFirstChild: true,
            children: [
              {
                name: 'compose',
                next: 'tag'
            
              },
              {
                name: 'tag',
                prev: 'compose',
                next: 'roles'
              },
              {
                name: 'roles',
                prev: 'tag',
                next: 'event'
              },              
              {
                name: 'schedule',
                prev: 'roles',
                next: 'share'
              },
              {
                name: 'share',
                prev: 'schedule'
              }
         
            ]
          },
          {
            name: '_import',
            modal: true,
            delegateToFirstChild: true,
            children: [
              {
                name: 'public',
                modal: true
              },
              {
                name: 'group',
                modal: true,
                path: ':group'
              }
            ]
          }
        ]
      },
    
    ]
  },
  {
    name: 'live-book-cair-desktop',
    path: 'live-book-cair-desktop/:post',
    modal: true
  },
  {
    name: 'live-book-cair-mobile',
    path: 'live-book-cair-mobile/:post',
    modal: true
  },
  {
    name: 'book-cair-browse',
    path: 'book-cair-browse/:group'
  },
  {
    name: 'book-cair-profile',
    path: 'book-cair-profile'
  },
  {
    name: 'book-cair-library',
    path: 'book-cair-library'
  },
  {
    name: 'book-cair-purchases',
    path: 'book-cair-purchases'
  },
  {
    name: 'book-cair-dashboard',
    path: 'book-cair-dashboard/:circleId?'
  },
  {
    name: 'book-cair-payment',
    path: 'book-cair-payment'
  },
  {
    name: 'book-cair-payment-result',
    path: 'book-cair-payment-result'
  },
  {
    name: 'landing',
    path: 'landing'
  },
  {
    name: NOT_FOUND
  }
]

const init = opts => {
  const prepareAsyncData = (page: AsyncPage) => async (dispatch, getState) => {
    await page.prepareAsyncData(dispatch, getState, opts)
  }
  return {
    routes: createRoutesMap(ROUTE_CONFIGURATION, prepareAsyncData, PAGES),
    nav_hierarchy: createNavigationHierarchy(ROUTE_CONFIGURATION)
  }
}

export default init
